import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';

/** Dato societario mostrato nel footer: etichetta e valore vengono dai file di traduzione */
interface LegalItem {
  labelKey: string;
  valueKey: string;
  icon: string;
}

/**
 * Footer comune ai layout pubblico e privato.
 * Riporta i dati societari richiesti dall'art. 2250 c.c. per i siti web delle società di capitali
 * (denominazione, sede legale, registro imprese/REA, capitale sociale, partita IVA e codice fiscale)
 * più i recapiti. I valori stanno in FOOTER.LEGAL.* dei file i18n, così si aggiornano senza deploy;
 * una voce con valore vuoto non viene mostrata.
 */
@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css'
})
export class FooterComponent {
  readonly currentYear = new Date().getFullYear();

  readonly legalItems: LegalItem[] = [
    { labelKey: 'FOOTER.LEGAL.SEAT.LABEL', valueKey: 'FOOTER.LEGAL.SEAT.VALUE', icon: 'fa-location-dot' },
    { labelKey: 'FOOTER.LEGAL.VAT.LABEL', valueKey: 'FOOTER.LEGAL.VAT.VALUE', icon: 'fa-file-invoice' },
    { labelKey: 'FOOTER.LEGAL.FISCAL_CODE.LABEL', valueKey: 'FOOTER.LEGAL.FISCAL_CODE.VALUE', icon: 'fa-id-card' },
    { labelKey: 'FOOTER.LEGAL.REA.LABEL', valueKey: 'FOOTER.LEGAL.REA.VALUE', icon: 'fa-building' },
    { labelKey: 'FOOTER.LEGAL.CAPITAL.LABEL', valueKey: 'FOOTER.LEGAL.CAPITAL.VALUE', icon: 'fa-coins' },
    { labelKey: 'FOOTER.LEGAL.PEC.LABEL', valueKey: 'FOOTER.LEGAL.PEC.VALUE', icon: 'fa-envelope-circle-check' },
    { labelKey: 'FOOTER.LEGAL.EMAIL.LABEL', valueKey: 'FOOTER.LEGAL.EMAIL.VALUE', icon: 'fa-envelope' },
    { labelKey: 'FOOTER.LEGAL.PHONE.LABEL', valueKey: 'FOOTER.LEGAL.PHONE.VALUE', icon: 'fa-phone' }
  ];

  /** Link legali esterni (informative pubblicate dall'azienda); un link con URL vuoto non viene mostrato */
  readonly legalLinks: { labelKey: string; urlKey: string; icon: string }[] = [
    { labelKey: 'FOOTER.LINKS.PRIVACY.LABEL', urlKey: 'FOOTER.LINKS.PRIVACY.URL', icon: 'fa-shield-halved' },
    { labelKey: 'FOOTER.LINKS.COOKIES.LABEL', urlKey: 'FOOTER.LINKS.COOKIES.URL', icon: 'fa-cookie-bite' }
  ];

  constructor(
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  /** Valore tradotto di una chiave; stringa vuota se la chiave manca o è vuota (la voce non si mostra) */
  legalValue(key: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? '' : value;
  }

  /** True se il valore è un indirizzo e-mail: viene reso come link mailto */
  isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  /**
   * Link alla pagina Termini di servizio e Privacy. La pagina è registrata in
   * entrambi i layout: /tos nel pubblico (PublicGuard rimanda alla dashboard chi è
   * loggato) e /private/tos nel privato (AuthGuard rimanda al login chi non lo è).
   */
  get tosLink(): string {
    return this.authService.hasLongLivedToken() ? '/private/tos' : '/tos';
  }

  changeLanguage(lang: string): void {
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
    localStorage.setItem('language', lang);
  }
}
